"use client";

import Image from "next/image";
import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { HiOutlineChevronDown } from "react-icons/hi2";
import { IconType } from "react-icons";

export interface NavLinkProps {
  href?: string;
  label: string;
  icon?: IconType;
  isExternal?: boolean;
  className?: string;
  customIcon?: string;
  dropdownItems?: Array<{
    section?: string;
    icon?: IconType;
    customIcon?: string;
    items?: Array<{
      href: string;
      label: string;
      description?: string;
      isExternal?: boolean;
    }>;
    // Keep the old format for non-section items
    href?: string;
    label?: string;
    description?: string;
    isExternal?: boolean;
  }>;
  onClick?: () => void;
}

export function NavLink({
  href,
  label,
  isExternal = false,
  dropdownItems,
  icon: Icon,
  customIcon,
  className = "",
  onClick,
}: NavLinkProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleTriggerMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as Node | null;
    if (
      relatedTarget &&
      dropdownRef.current &&
      !dropdownRef.current.contains(relatedTarget)
    ) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 100);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    setIsOpen(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const commonClasses = `flex group items-center text-sm font-medium text-text-link-nav-default hover:text-text-link-nav-hover hover:bg-surface-link-nav-hover transition-colors duration-200 rounded-md ${className}`;

  const renderIcon = () => {
    if (customIcon) {
      return (
        <Image
          src={customIcon}
          alt={`${label} icon`}
          width={16}
          height={16}
          className="mr-2"
        />
      );
    }
    if (Icon) {
      return <Icon className="w-5 h-5 mr-2" />;
    }
    return null;
  };

  if (dropdownItems) {
    return (
      <div className="relative">
        <button
          className={`${commonClasses} px-3 py-1`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
        >
          {renderIcon()}
          {label} <HiOutlineChevronDown className="ml-2 w-5 h-5" />
        </button>
        {isOpen && (
          <div
            ref={dropdownRef}
            className={`absolute -left-1 mt-5 ${
              dropdownItems[0].section ? "w-[480px] max-w-[95vw] sm:max-w-[480px]" : "w-[200px]"
            } rounded-lg shadow-lg bg-card/95 backdrop-blur-sm border border-border overflow-hidden`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className={`p-0.5 ${
                dropdownItems[0].section ? "grid grid-cols-1 sm:grid-cols-2" : ""
              }`}
              role="menu"
              aria-orientation="vertical"
            >
              {dropdownItems[0].section ? (
                // Sectioned layout (Products)
                dropdownItems.map((section, idx) => (
                  <div
                    key={idx}
                    className={`py-2 px-2 max-w-[240px] ${
                      idx === 0 ? "sm:border-r border-border-hover" : ""
                    }`}
                  >
                    <div className="flex items-center mb-2 ml-1 mt-1 text-sm font-medium text-text-body">
                      {section.customIcon ? (
                        <Image
                          src={section.customIcon}
                          alt={`${section.section} icon`}
                          width={16}
                          height={16}
                          className="mr-2"
                        />
                      ) : (
                        section.icon && (
                          <section.icon className="w-5 h-5 mr-2 text-icon-secondary-default" />
                        )
                      )}
                      <span className="text-sm font-medium text-text-body-secondary">
                        {section.section}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {section.items?.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          target={item.isExternal ? "_blank" : undefined}
                          rel={
                            item.isExternal ? "noopener noreferrer" : undefined
                          }
                          className={`${commonClasses} px-2.5 py-2 group flex flex-col items-stretch`}
                          onClick={handleClick}
                        >
                          <span className="font-medium mb-0.5">
                            {item.label}
                          </span>
                          {item.description && (
                            <p className="text-xs text-text-body-secondary">
                              {item.description}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                // Simple layout (Company)
                <div className="py-1 px-1.5">
                  {dropdownItems.map((item, idx) => (
                    <Link
                      key={idx}
                      href={item.href!}
                      target={item.isExternal ? "_blank" : undefined}
                      rel={item.isExternal ? "noopener noreferrer" : undefined}
                      className={`${commonClasses} px-3 py-2 flex items-center`}
                      onClick={handleClick}
                    >
                      {item.icon && (
                        <item.icon className="text-icon-secondary-default group-hover:text-icon-secondary-hover w-5 h-5 mr-2" />
                      )}
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      href={href!}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className={`${commonClasses} px-3 py-1`}
      onClick={handleClick}
    >
      {renderIcon()}
      {label}
    </Link>
  );
}
